/**
 * Manager Assistant phone + PIN auth (mirrors client-auth.ts, for firm users).
 * Talks to /ma-auth/* and stores its own JWT in localStorage('ma-auth-token').
 */
import axios from 'axios';
import { useEffect, useState } from 'react';
import { cognitoAuth } from '../auth/cognito';

const API_BASE = import.meta.env.VITE_API_URL || 'https://roniapi.data-corner.com.au';
const TOKEN_KEY = 'ma-auth-token';
const MANAGER_KEY = 'ma-auth-manager';
const REMEMBERED_PHONE_KEY = 'ma-remembered-phone';
const HAS_PIN_KEY = 'ma-has-pin';
const PIN_UNLOCKED_KEY = 'ma-pin-unlocked'; // sessionStorage - cleared when tab/app closes

export interface ManagerUser {
  id: string;
  tenantId: string;
  displayName: string;
  email?: string | null;
  phone?: string | null;
  role?: string | null;
}

const api = axios.create({ baseURL: API_BASE });

export function getToken(): string | null { return localStorage.getItem(TOKEN_KEY); }
export function isAuthenticated(): boolean { return !!localStorage.getItem(TOKEN_KEY); }
export function getManager(): ManagerUser | null {
  try { const r = localStorage.getItem(MANAGER_KEY); return r ? JSON.parse(r) : null; } catch { return null; }
}
export function getRememberedPhone(): string { return localStorage.getItem(REMEMBERED_PHONE_KEY) || ''; }

export function getHasPinCached(): boolean { return localStorage.getItem(HAS_PIN_KEY) === '1'; }
export function setHasPinCached(v: boolean): void { v ? localStorage.setItem(HAS_PIN_KEY, '1') : localStorage.removeItem(HAS_PIN_KEY); }
export function isPinUnlocked(): boolean { try { return sessionStorage.getItem(PIN_UNLOCKED_KEY) === '1'; } catch { return false; } }
export function markPinUnlocked(): void { try { sessionStorage.setItem(PIN_UNLOCKED_KEY, '1'); } catch {} }
export function clearPinUnlocked(): void { try { sessionStorage.removeItem(PIN_UNLOCKED_KEY); } catch {} }

function store(token: string, manager: ManagerUser, phone: string) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(MANAGER_KEY, JSON.stringify(manager));
  localStorage.setItem(REMEMBERED_PHONE_KEY, phone);
  markPinUnlocked(); // SMS / PIN login is a full unlock for this session
}

export async function requestCode(phone: string): Promise<void> {
  await api.post('/ma-auth/request-code', { phone });
}

export async function verifyCode(phone: string, code: string): Promise<ManagerUser> {
  const r = await api.post<{ token: string; manager: ManagerUser }>('/ma-auth/verify-code', { phone, code });
  store(r.data.token, r.data.manager, phone);
  return r.data.manager;
}

export async function loginWithPin(phone: string, pin: string): Promise<ManagerUser> {
  const r = await api.post<{ token: string; manager: ManagerUser }>('/ma-auth/login-pin', { phone, pin });
  store(r.data.token, r.data.manager, phone);
  setHasPinCached(true);
  return r.data.manager;
}

/** Email + password login: authenticate against the firm Cognito pool, then
 * exchange the access token for a manager JWT (server checks eligibility). */
export async function loginWithEmail(email: string, password: string): Promise<ManagerUser> {
  const res = await cognitoAuth.signIn(email, password);
  if (!res.success) {
    if (res.requiresMFA) throw new Error('This account uses MFA. Please sign in with your phone instead.');
    throw new Error(res.error || 'Incorrect email or password');
  }
  const accessToken = res.session?.getAccessToken().getJwtToken() || (await cognitoAuth.getAccessToken());
  if (!accessToken) throw new Error('Sign-in failed, please try again');
  const r = await api.post<{ token: string; manager: ManagerUser }>('/ma-auth/exchange', { accessToken });
  store(r.data.token, r.data.manager, r.data.manager.phone || '');
  return r.data.manager;
}

export async function setPin(pin: string): Promise<void> {
  await api.post('/ma-auth/set-pin', { pin }, { headers: { Authorization: `Bearer ${getToken()}` } });
  setHasPinCached(true);
}

export async function deletePin(): Promise<void> {
  await api.post('/ma-auth/delete-pin', {}, { headers: { Authorization: `Bearer ${getToken()}` } });
  setHasPinCached(false);
}

export async function fetchMe(): Promise<{ manager: ManagerUser; hasPin: boolean } | null> {
  try {
    const r = await api.get<{ manager: ManagerUser; hasPin: boolean }>('/ma-auth/me', { headers: { Authorization: `Bearer ${getToken()}` } });
    if (r.data?.manager) localStorage.setItem(MANAGER_KEY, JSON.stringify(r.data.manager));
    setHasPinCached(!!r.data?.hasPin);
    return r.data;
  } catch { return null; }
}

export function logout(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(MANAGER_KEY);
  clearPinUnlocked();
  // keep remembered phone + has-pin so the next login is quick
}

/** Reactive manager identity for components. */
export function useManager(): ManagerUser | null {
  const [m, setM] = useState<ManagerUser | null>(getManager);
  useEffect(() => {
    const onStorage = () => setM(getManager());
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  return m;
}
